import * as api from "@/api";
import { getSelection, getValue, setValue } from "@/code-editor";
import styles from "@/components/ai-bar.module.css";
import AiIcon from "@/components/ai-icon";
import ChatMessage from "@/components/chat-message";
import SidebarShell from "@/components/sidebar-shell";
import { handleSaveClick } from "@/file";
import globals from "@/globals";
import * as signals from "@/signals";
import { files } from "@/signals";
import { BinaryFile, SiteConfig } from "@/types";
import { closeAiBar, isPremium, showDialog, sleep } from "@/utils";
import { faCircleExclamation } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { t } from "i18next";
import { ChangeEvent, JSX, useId, useRef, useState } from "react";

type AiRequestType =
	| "writeNewHtmlCode"
	| "editExistingHtmlCode"
	| "askQuestion"
	| "askQuestionAboutCurrentSiteCode";

function AiBar(): JSX.Element {
	const [aiResponse, setAiResponse] = useState("");
	const [isAiLoading, setIsAiLoading] = useState(false);
	const [requestText, setRequestText] = useState("");
	const [type, setType] = useState<AiRequestType>("writeNewHtmlCode");

	const messageArea = useRef<HTMLDivElement>(null);

	const aiRequestId = useId();
	const askQuestionId = useId();
	const askQuestionAboutCurrentSiteCodeId = useId();
	const editExistingCodeId = useId();
	const writeNewCodeId = useId();

	const askQuestion = async (): Promise<void> => {
		if (!isPremium()) {
			return;
		}
		let question = "";
		const selection = getSelection();
		if (selection) {
			question = t("codeUserSelected") + "\n" + selection + "\n";
		}
		if (type === "askQuestionAboutCurrentSiteCode") {
			question += t("thisIsMyCurrentCode") + "\n";
			const filesCopy: Record<
				string,
				string | BinaryFile | SiteConfig | undefined
			> = {
				...signals.files.value,
			};
			delete filesCopy["settings.rth"];
			const siteContent = JSON.stringify(filesCopy);
			if (siteContent.length <= 128_000) {
				question +=
					siteContent +
					"\n" +
					t("currentFile") +
					signals.currentFile.value +
					"\n";
			} else {
				question += getValue() + "\n" + t("thisIsFileList") + "\n";
				for (const key in files) {
					question += key + "\n";
				}
			}
		}
		if (question) {
			question += "\n";
		}
		question += requestText;
		setAiResponse("");
		setIsAiLoading(true);
		try {
			await api.sendMessageToAi({
				callback: (response: string) => {
					const { message } = splitThinking(response);
					setAiResponse(message);
					if (messageArea.current) {
						messageArea.current.scrollTop =
							messageArea.current.scrollHeight;
					}
				},
				message: question,
				mode: "host-open",
			});
		} catch (error) {
			console.error(error);
			await sleep(globals.ANIMATION_WAIT_TIME);
			void showDialog(t("aiEncounteredDifficultiesRetry"));
		} finally {
			setIsAiLoading(false);
		}
	};

	const generateCode = async (): Promise<void> => {
		if (!isPremium()) {
			return;
		}
		if (!/(\.html?|\/)$/.test(signals.currentFile.value)) {
			void showDialog(t("currentFileIsNotHtmlFile"));
			return;
		}
		await showDialog(t("thisFileWillBeReplacedWithCodeWrittenByAi"), {
			countdown: 3,
			showCancel: true,
		});
		setAiResponse("");
		setIsAiLoading(true);
		try {
			await api.sendMessageToAi({
				callback: (response: string) => {
					const { isThinking, message } = splitThinking(response);
					if (isThinking) {
						setAiResponse(message);
						if (messageArea.current) {
							messageArea.current.scrollTop =
								messageArea.current.scrollHeight;
						}
					} else {
						let output = message;
						if (
							message.includes("<html") &&
							message.includes("</html>")
						) {
							output =
								"<!DOCTYPE html>\n<html" +
								message.split("<html")[1].split("</html>")[0] +
								"</html>";
						} else if (message.includes("```html")) {
							output = message
								.split("```html")[1]
								.split("```")[0];
						}
						output = output.replace(/^\s+/, "");
						setValue(output, {
							willScrollToBottom: true,
						});
					}
				},
				message:
					requestText +
					(type === "editExistingHtmlCode"
						? `\n${t("thisIsMyCurrentCode")}\n${getValue()}`
						: ""),
				mode: "host",
			});
			handleSaveClick();
		} catch (error) {
			console.error(error);
			await sleep(globals.ANIMATION_WAIT_TIME);
			void showDialog(t("aiEncounteredDifficultiesRetry"));
		} finally {
			setIsAiLoading(false);
		}
	};

	const handleRequestTextChange = (
		event: ChangeEvent<HTMLTextAreaElement>,
	): void => {
		setRequestText(event.target.value);
	};

	const handleTypeChange = (event: ChangeEvent<HTMLInputElement>): void => {
		setType(event.target.value as AiRequestType);
	};

	const reset = (): void => {
		setAiResponse("");
		setRequestText("");
	};

	const splitThinking = (
		message: string,
	): {
		isThinking: boolean;
		message: string;
	} => {
		if (!message.includes("<think>")) {
			return {
				isThinking: false,
				message,
			};
		}
		const parts = message.split("</think>");
		if (parts.length > 1) {
			return {
				isThinking: false,
				message: parts[parts.length - 1].replace(/^\s+/, ""),
			};
		}
		return {
			isThinking: true,
			message: parts[0].replace(/<\/?think>/g, ""),
		};
	};

	const aiActions = [
		{
			id: writeNewCodeId,
			label: "writeNewHtmlCode",
			value: "writeNewHtmlCode",
		},
		{
			id: editExistingCodeId,
			label: "editExistingHtmlCode",
			value: "editExistingHtmlCode",
		},
		{
			description: t("askQuestionDescription"),
			id: askQuestionId,
			label: "askQuestion",
			value: "askQuestion",
		},
		{
			description: t("askQuestionAboutCurrentSiteCodeDescription"),
			id: askQuestionAboutCurrentSiteCodeId,
			label: "askQuestionAboutCurrentSiteCode",
			value: "askQuestionAboutCurrentSiteCode",
		},
	];

	const aiActionElements = aiActions.map((action) => {
		return (
			<div
				className={styles["input-bar"]}
				key={action.id}
			>
				<input
					checked={type === action.value}
					id={action.id}
					name="ai-action"
					onChange={handleTypeChange}
					type="radio"
					value={action.value}
				/>
				<label htmlFor={action.id}>
					{t(action.label)}
					{action.description && (
						<div className={styles["input-bar-description"]}>
							{t(action.description)}
						</div>
					)}
				</label>
			</div>
		);
	});

	return (
		<SidebarShell
			onClose={closeAiBar}
			title={
				<>
					<AiIcon className="icon" />
					{aiResponse || isAiLoading ? (
						<div className="text">{t("aiProgrammer")}</div>
					) : (
						<label
							className="text"
							htmlFor={aiRequestId}
						>
							{t("describeYourNeeds")}
						</label>
					)}
				</>
			}
		>
			<>
				{aiResponse || isAiLoading ? (
					<div
						className={styles["message-area"]}
						ref={messageArea}
					>
						<ChatMessage
							isLoading={false}
							message={requestText}
						/>
						<ChatMessage
							isLoading={isAiLoading && !aiResponse}
							message={aiResponse}
						/>
					</div>
				) : (
					<>
						<textarea
							className={clsx("input-box", styles["input-box"])}
							id={aiRequestId}
							onChange={handleRequestTextChange}
							onClick={() => void isPremium()}
							placeholder={t("aiRequestExample")}
							value={requestText}
						></textarea>
						{aiActionElements}
					</>
				)}
				<p className={styles["warning-box"]}>
					<FontAwesomeIcon icon={faCircleExclamation} />
					{t("aiProgrammerDisclaimer")}
				</p>
				{aiResponse || isAiLoading ? (
					<button
						className="button"
						disabled={isAiLoading}
						type="button"
						onClick={reset}
					>
						{t("newQuestion")}
					</button>
				) : type === "askQuestion" ||
				  type === "askQuestionAboutCurrentSiteCode" ? (
					<button
						className="default-button"
						disabled={!requestText}
						type="submit"
						onClick={() => void askQuestion()}
					>
						{t("send")}
					</button>
				) : (
					<button
						className="default-button"
						disabled={!requestText}
						type="submit"
						onClick={() => void generateCode()}
					>
						{t("generate")}
					</button>
				)}
			</>
		</SidebarShell>
	);
}

export default AiBar;
